/**
 * Auction on-chain actions (Macro #29 Phase 9) — seller createAuction, bidder
 * bid (approve + bid), permissionless settle, seller cancel. Every economic
 * permit + signature is backend-authoritative; the connected wallet is the only
 * broadcaster. Bidder funds custody uses USDC approve to the Auction Escrow.
 */
import { useCallback, useRef, useState } from "react";
import { usePublicClient, useWriteContract } from "wagmi";

import { ERC20_APPROVE_ABI, isUserRejection } from "../../lib/usdc-approve";
import { ERC20_ALLOWANCE_ABI } from "./buy-settlement";
import { marketplaceApi } from "../../lib/marketplace-api";
import {
  AUCTION_ESCROW_ABI,
  buildAuctionBidPermit,
  buildAuctionCreatePermit,
} from "./artwork-escrow-abi";

export type OnchainStage =
  | "prepare" | "allowance" | "approve" | "create" | "bid" | "settle" | "cancel";

export type OnchainState =
  | { kind: "idle" }
  | { kind: "preparing" }
  | { kind: "checking_allowance" }
  | { kind: "awaiting_approve_wallet" }
  | { kind: "approving"; txHash: `0x${string}` }
  | { kind: "awaiting_wallet"; stage: OnchainStage }
  | { kind: "submitting"; stage: OnchainStage; txHash: `0x${string}` }
  | { kind: "completed"; txHash: `0x${string}` }
  | { kind: "rejected"; stage: OnchainStage }
  | { kind: "reverted"; stage: OnchainStage; txHash: `0x${string}` }
  | { kind: "error"; stage: OnchainStage; error: string };

const PENDING: ReadonlyArray<OnchainState["kind"]> = [
  "preparing", "checking_allowance", "awaiting_approve_wallet", "approving",
  "awaiting_wallet", "submitting",
];

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

interface OnchainRunner {
  state: OnchainState;
  reset: () => void;
  isPending: boolean;
}

function useOnchainRunner(): OnchainRunner & {
  run: (fn: (deps: RunDeps) => Promise<void>) => Promise<void>;
} {
  const [state, setState] = useState<OnchainState>({ kind: "idle" });
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();
  const runningRef = useRef(false);
  const isPending = PENDING.includes(state.kind);

  const reset = useCallback(() => {
    if (runningRef.current) return;
    setState({ kind: "idle" });
  }, []);

  const run = useCallback(
    async (fn: (deps: RunDeps) => Promise<void>) => {
      if (runningRef.current) return;
      if (publicClient == null) {
        setState({ kind: "error", stage: "prepare", error: "public_client_not_ready" });
        return;
      }
      runningRef.current = true;
      const pc = publicClient;
      try {
        await fn({
          setState,
          write: (p) => writeContractAsync(p as never) as Promise<`0x${string}`>,
          waitReceipt: async (hash) => {
            const rc = await pc.waitForTransactionReceipt({ hash });
            return rc.status === "success" ? "success" : "reverted";
          },
          readAllowance: async (usdc, owner, spender) =>
            (await pc.readContract({
              address: usdc, abi: ERC20_ALLOWANCE_ABI, functionName: "allowance",
              args: [owner, spender],
            })) as bigint,
        });
      } catch (e) {
        setState((s) => (s.kind === "idle" || s.kind === "completed" ? s
          : { kind: "error", stage: "prepare", error: errMsg(e) }));
      } finally {
        runningRef.current = false;
      }
    },
    [publicClient, writeContractAsync],
  );

  return { state, reset, isPending, run };
}

interface RunDeps {
  setState: (s: OnchainState) => void;
  write: (p: Record<string, unknown>) => Promise<`0x${string}`>;
  waitReceipt: (hash: `0x${string}`) => Promise<"success" | "reverted">;
  readAllowance: (usdc: `0x${string}`, owner: `0x${string}`, spender: `0x${string}`) => Promise<bigint>;
}

// --- CREATE AUCTION (seller signs createAuction on-chain) ---
export interface CreateAuctionInput {
  body: {
    artwork_id: string; currency: string; reserve_micro: number; min_increment_micro?: number;
    start_market_ts: number; end_market_ts: number; anti_snipe_window?: number; nonce?: number;
  };
  escrow: `0x${string}`;
  chainId: number;
}

export function useCreateAuction() {
  const r = useOnchainRunner();
  const create = useCallback(
    (input: CreateAuctionInput) =>
      r.run(async ({ setState, write, waitReceipt }) => {
        setState({ kind: "preparing" });
        const res = await marketplaceApi.createAuction(input.body);
        setState({ kind: "awaiting_wallet", stage: "create" });
        let hash: `0x${string}`;
        try {
          hash = await write({
            address: input.escrow, abi: AUCTION_ESCROW_ABI, functionName: "createAuction",
            args: [buildAuctionCreatePermit(res.permit), res.signature as `0x${string}`],
            chainId: input.chainId,
          });
        } catch (e) {
          setState(isUserRejection(e) ? { kind: "rejected", stage: "create" }
            : { kind: "error", stage: "create", error: errMsg(e) });
          return;
        }
        setState({ kind: "submitting", stage: "create", txHash: hash });
        setState((await waitReceipt(hash)) === "success"
          ? { kind: "completed", txHash: hash }
          : { kind: "reverted", stage: "create", txHash: hash });
      }),
    [r],
  );
  return { state: r.state, reset: r.reset, isPending: r.isPending, create };
}

// --- BID (approve + bid) ---
export interface BidInput {
  auctionId: string;
  amountMicro: number;
  escrow: `0x${string}`;
  usdc: `0x${string}`;
  bidder: `0x${string}`;
  chainId: number;
}

export function useBidOnAuction() {
  const r = useOnchainRunner();
  const placeBid = useCallback(
    (input: BidInput) =>
      r.run(async ({ setState, write, waitReceipt, readAllowance }) => {
        setState({ kind: "preparing" });
        const res = await marketplaceApi.placeBid(input.auctionId, {
          amount_micro: input.amountMicro, nonce: Date.now(),
        });
        const amount = BigInt(input.amountMicro);
        setState({ kind: "checking_allowance" });
        const allowance = await readAllowance(input.usdc, input.bidder, input.escrow);
        if (allowance < amount) {
          setState({ kind: "awaiting_approve_wallet" });
          let ah: `0x${string}`;
          try {
            ah = await write({
              address: input.usdc, abi: ERC20_APPROVE_ABI, functionName: "approve",
              args: [input.escrow, amount], chainId: input.chainId,
            });
          } catch (e) {
            setState(isUserRejection(e) ? { kind: "rejected", stage: "approve" }
              : { kind: "error", stage: "approve", error: errMsg(e) });
            return;
          }
          setState({ kind: "approving", txHash: ah });
          if ((await waitReceipt(ah)) !== "success") {
            setState({ kind: "reverted", stage: "approve", txHash: ah });
            return;
          }
        }
        setState({ kind: "awaiting_wallet", stage: "bid" });
        let hash: `0x${string}`;
        try {
          hash = await write({
            address: input.escrow, abi: AUCTION_ESCROW_ABI, functionName: "bid",
            args: [buildAuctionBidPermit(res.permit), res.signature as `0x${string}`],
            chainId: input.chainId,
          });
        } catch (e) {
          setState(isUserRejection(e) ? { kind: "rejected", stage: "bid" }
            : { kind: "error", stage: "bid", error: errMsg(e) });
          return;
        }
        setState({ kind: "submitting", stage: "bid", txHash: hash });
        setState((await waitReceipt(hash)) === "success"
          ? { kind: "completed", txHash: hash }
          : { kind: "reverted", stage: "bid", txHash: hash });
      }),
    [r],
  );
  return { state: r.state, reset: r.reset, isPending: r.isPending, placeBid };
}

// --- SETTLE (permissionless after end) ---
export function useSettleAuction() {
  const r = useOnchainRunner();
  const settle = useCallback(
    (input: { auctionId: string; escrow: `0x${string}`; chainId: number }) =>
      r.run(async ({ setState, write, waitReceipt }) => {
        setState({ kind: "awaiting_wallet", stage: "settle" });
        let hash: `0x${string}`;
        try {
          hash = await write({
            address: input.escrow, abi: AUCTION_ESCROW_ABI, functionName: "settle",
            args: [input.auctionId as `0x${string}`], chainId: input.chainId,
          });
        } catch (e) {
          setState(isUserRejection(e) ? { kind: "rejected", stage: "settle" }
            : { kind: "error", stage: "settle", error: errMsg(e) });
          return;
        }
        setState({ kind: "submitting", stage: "settle", txHash: hash });
        setState((await waitReceipt(hash)) === "success"
          ? { kind: "completed", txHash: hash }
          : { kind: "reverted", stage: "settle", txHash: hash });
      }),
    [r],
  );
  return { state: r.state, reset: r.reset, isPending: r.isPending, settle };
}

// --- CANCEL (seller, bid-less; backend validates first) ---
export function useCancelAuction() {
  const r = useOnchainRunner();
  const cancel = useCallback(
    (input: { auctionId: string; escrow: `0x${string}`; chainId: number }) =>
      r.run(async ({ setState, write, waitReceipt }) => {
        setState({ kind: "preparing" });
        await marketplaceApi.cancelAuction(input.auctionId);
        setState({ kind: "awaiting_wallet", stage: "cancel" });
        let hash: `0x${string}`;
        try {
          hash = await write({
            address: input.escrow, abi: AUCTION_ESCROW_ABI, functionName: "cancel",
            args: [input.auctionId as `0x${string}`], chainId: input.chainId,
          });
        } catch (e) {
          setState(isUserRejection(e) ? { kind: "rejected", stage: "cancel" }
            : { kind: "error", stage: "cancel", error: errMsg(e) });
          return;
        }
        setState({ kind: "submitting", stage: "cancel", txHash: hash });
        setState((await waitReceipt(hash)) === "success"
          ? { kind: "completed", txHash: hash }
          : { kind: "reverted", stage: "cancel", txHash: hash });
      }),
    [r],
  );
  return { state: r.state, reset: r.reset, isPending: r.isPending, cancel };
}
